import {
  createContext,
  MouseEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { CircularProgress } from '@/UI/CircularProgress/CircularProgress';
import { Typography } from '@/UI/AppTypography/AppTypography';
import { styled } from '@/UI/theme.exports';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { color } from '@/colors';
import ParentMenuItem from '@/components/Recipe/Menu/ParentMenuItem';
import { FIGMA_EMBED_MESSAGE_TYPES } from '@/consts/figma-embed-messages';
import { isFigmaEmbedAllowedOrigin } from '@/consts/auth.consts';
import { useFigmaEmbedAiMeterHasCredits, useIsFigmaEmbedMode } from '@/contexts/FigmaEmbedModeContext';
import { I18N_KEYS } from '@/language/keys';

const AI_BALANCE_SECONDARY_TEXT_COLOR = 'rgba(255, 255, 255, 0.7)';
const AI_BALANCE_CARD_HORIZONTAL_PADDING = '12.8px';
const AI_BALANCE_MENU_ITEM_HORIZONTAL_PADDING = '3px';

const aiBalanceTextBodyStyles = {
  fontSize: '11px',
  letterSpacing: 0,
  lineHeight: '16px',
} as const;

const aiBalanceTextBodyStrongStyles = {
  ...aiBalanceTextBodyStyles,
  fontWeight: 550,
} as const;

const aiBalanceMenuItemStyles = {
  cursor: 'default',
  listStyle: 'none',
  outline: 'none',
  width: '100%',
  wordBreak: 'break-word',
} as const;

const aiBalanceContainerShadow =
  '0px 1px 3px rgba(0, 0, 0, 0.4), inset 0px 0px 0.5px rgba(255, 255, 255, 0.3), inset 0px 0.5px 0px rgba(255, 255, 255, 0.1), 0px 0px 0.5px rgba(0, 0, 0, 0.5)';

const AiBalanceActionButtonRoot = styled(ButtonContained)({
  '&&': {
    backgroundColor: color.White100,
    borderRadius: '6px',
    color: color.Black100,
    textTransform: 'none',
  },
  '&&:hover': {
    backgroundColor: color.White80_T,
  },
});

const AiBalanceText = styled(Typography)({
  ...aiBalanceTextBodyStyles,
  color: 'inherit',
  display: 'block',
});

const AiBalanceMenuItem = styled('li')({
  ...aiBalanceMenuItemStyles,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  paddingBottom: '4px',
  paddingLeft: AI_BALANCE_MENU_ITEM_HORIZONTAL_PADDING,
  paddingRight: AI_BALANCE_MENU_ITEM_HORIZONTAL_PADDING,
  paddingTop: '6px',
});

const AiBalanceLoadingMenuItem = styled(AiBalanceMenuItem)({
  alignItems: 'center',
  gap: 0,
  justifyContent: 'center',
  minHeight: '100px',
});

const AiBalanceErrorMenuItem = styled(AiBalanceMenuItem)({
  color: token.textSecondary,
  gap: 0,
  paddingBottom: '6px',
});

const AiBalanceErrorHeading = styled(Typography)({
  color: token.textSecondary,
  display: 'block',
  fontSize: '12px',
  fontWeight: 700,
  lineHeight: '16px',
});

const AiBalanceErrorBody = styled(Typography)({
  color: token.textSecondary,
  display: 'block',
  fontSize: '12px',
  lineHeight: '16px',
});

const AiBalanceCardHeader = styled('div')({
  ...aiBalanceTextBodyStyles,
  color: token.text,
});

// The out-of-credits header sits above the card. Its divider bleeds out past the
// menu item's horizontal padding and the enclosing popover paper's horizontal
// padding (theme.spacing(0.5), matching the `px: 0.5` on the AppContextMenu paper)
// so the line spans the full popover width instead of stopping short of both edges.
const AiBalanceDivider = styled('div')(({ theme }) => {
  const bleed = `calc(-1 * (${AI_BALANCE_MENU_ITEM_HORIZONTAL_PADDING} + ${theme.spacing(0.5)}))`;
  return {
    alignSelf: 'stretch',
    backgroundColor: color.White16_T,
    height: '1px',
    marginLeft: bleed,
    marginRight: bleed,
  };
});

const AiBalanceCardShell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isEmptyBalance',
})<{ isEmptyBalance: boolean }>(({ isEmptyBalance }) => ({
  backgroundColor: isEmptyBalance ? token.bg : token.bgSecondary,
  border: isEmptyBalance ? `1px solid ${token.border}` : '1px solid transparent',
  borderRadius: '5px',
  boxShadow: isEmptyBalance ? 'none' : aiBalanceContainerShadow,
  paddingBottom: '16px',
  paddingLeft: AI_BALANCE_CARD_HORIZONTAL_PADDING,
  paddingRight: AI_BALANCE_CARD_HORIZONTAL_PADDING,
  paddingTop: '12px',
}));

const AiBalanceCardTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'hasSubtitle' && prop !== 'isEmptyBalance',
})<{ hasSubtitle: boolean; isEmptyBalance: boolean }>(({ hasSubtitle, isEmptyBalance }) => ({
  ...aiBalanceTextBodyStrongStyles,
  color: isEmptyBalance ? AI_BALANCE_SECONDARY_TEXT_COLOR : token.text,
  display: 'block',
  marginBottom: hasSubtitle ? 0 : '8px',
}));

const AiBalanceCardSubtitle = styled(Typography)({
  ...aiBalanceTextBodyStyles,
  color: AI_BALANCE_SECONDARY_TEXT_COLOR,
  display: 'block',
  marginBottom: '8px',
});

const AiBalanceMeterTrack = styled(Box)({
  backgroundColor: color.White16_T,
  borderRadius: '13px',
  height: '6px',
  overflow: 'hidden',
  width: '100%',
});

const AiBalanceMeterFill = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fillPercent',
})<{ fillPercent: number }>(({ fillPercent }) => ({
  backgroundColor: color.White100,
  borderRadius: 'inherit',
  height: '100%',
  width: `${fillPercent}%`,
}));

const AiBalanceBodyStack = styled('div')({
  ...aiBalanceTextBodyStyles,
  color: AI_BALANCE_SECONDARY_TEXT_COLOR,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const AiBalancePromotionalCredits = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

type AiBalanceMenuCardActionPlacement = 'body' | 'footer';

export type FigmaEmbedAiBalanceMenuCardAction =
  | {
      type: 'manage_credits';
      label: string;
      placement: AiBalanceMenuCardActionPlacement;
    }
  | {
      type: 'request_credits';
      label: string;
      placement: AiBalanceMenuCardActionPlacement;
    }
  | {
      type: 'pending_request';
      message: string;
      placement: AiBalanceMenuCardActionPlacement;
    }
  | {
      type: 'user_limit_request_credits';
      message: string;
      pendingMessage: string;
      hasPendingRequest: boolean;
      placement: AiBalanceMenuCardActionPlacement;
    };

export interface FigmaEmbedAiBalanceMenuCardViewModel {
  headerLines: string[];
  title: string;
  subtitle: string | null;
  hideMeter: boolean;
  meter: {
    value: number;
    max: number;
  };
  bodyLines: string[];
  promotionalFooterLines: string[];
  isEmptyBalance: boolean;
  action: FigmaEmbedAiBalanceMenuCardAction | null;
}

interface FigmaEmbedAiBalanceMenuProps {
  onClose: () => void;
}

type AiBalanceState =
  { kind: 'loading' } | { kind: 'error' } | { kind: 'ok'; viewModel: FigmaEmbedAiBalanceMenuCardViewModel };

interface FigmaEmbedAiBalanceCardProps {
  viewModel: FigmaEmbedAiBalanceMenuCardViewModel;
}

interface AiBalanceRefreshMessage {
  type: typeof FIGMA_EMBED_MESSAGE_TYPES.inbound.AI_BALANCE_REFRESH;
  viewModel?: FigmaEmbedAiBalanceMenuCardViewModel | null;
  loadFailed?: boolean;
}

function requestAiBalanceFromParent() {
  window.parent.postMessage({ type: FIGMA_EMBED_MESSAGE_TYPES.outbound.AI_BALANCE_REQUEST }, '*');
}

function postAiBalanceActionToParent(
  type:
    | typeof FIGMA_EMBED_MESSAGE_TYPES.outbound.AI_BALANCE_MANAGE_CREDITS
    | typeof FIGMA_EMBED_MESSAGE_TYPES.outbound.AI_BALANCE_REQUEST_CREDITS
    | typeof FIGMA_EMBED_MESSAGE_TYPES.outbound.AI_BALANCE_USER_LIMIT_REQUEST_CREDITS,
) {
  window.parent.postMessage({ type }, '*');
}

function getBoundedMeterValue(value: number, max: number) {
  const meterMax = Math.max(max, 0);
  const meterValue = meterMax === 0 ? 0 : Math.max(0, Math.min(value, meterMax));

  return {
    meterMax,
    meterValue,
    fillPercent: meterMax === 0 ? 0 : (meterValue / meterMax) * 100,
  };
}

function AiBalanceActionButton({
  children,
  onClick,
  disabled = false,
}: {
  children: string;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
}) {
  return (
    <AiBalanceActionButtonRoot fullWidth size="small" onClick={onClick} disabled={disabled}>
      {children}
    </AiBalanceActionButtonRoot>
  );
}

function AiBalanceActionView({ action }: { action: FigmaEmbedAiBalanceMenuCardAction }) {
  const { triggerAiBalanceAction } = useFigmaEmbedAiBalance();

  switch (action.type) {
    case 'pending_request':
      return <AiBalanceText variant="body-sm-rg">{action.message}</AiBalanceText>;
    case 'manage_credits':
    case 'request_credits':
      return (
        <AiBalanceActionButton
          onClick={(e) => {
            e.stopPropagation();
            triggerAiBalanceAction(action.type);
          }}
        >
          {action.label}
        </AiBalanceActionButton>
      );
    case 'user_limit_request_credits':
      return (
        <AiBalanceActionButton
          disabled={action.hasPendingRequest}
          onClick={
            action.hasPendingRequest
              ? undefined
              : (e) => {
                  e.stopPropagation();
                  triggerAiBalanceAction(action.type);
                }
          }
        >
          {action.hasPendingRequest ? action.pendingMessage : action.message}
        </AiBalanceActionButton>
      );
  }
}

function AiBalanceLoadingState() {
  const { t } = useTranslation();
  const loadingLabel = t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.FIGMA_EMBED_AI_BALANCE_LOADING_ARIA);
  return (
    <AiBalanceLoadingMenuItem>
      <CircularProgress
        aria-label={loadingLabel}
        size={16}
        thickness={5}
        sx={{ color: token.textSecondary }}
        fplProps={{ loadingText: loadingLabel }}
      />
    </AiBalanceLoadingMenuItem>
  );
}

function AiBalanceErrorState() {
  return (
    <AiBalanceErrorMenuItem>
      <AiBalanceErrorHeading variant="body-sm-rg">Couldn&apos;t load AI balance</AiBalanceErrorHeading>
      <AiBalanceErrorBody variant="body-sm-rg">Please refresh Figma to try again.</AiBalanceErrorBody>
    </AiBalanceErrorMenuItem>
  );
}

/**
 * Credits status card matching the Figma fullscreen menu's AI balance card
 * geometry. Figma sends the view model; Weavy only renders this menu surface.
 *
 * If changing layout, typography, spacing, meter styling, empty-state styling,
 * or action placement here, update the Figma source renderer in
 * web/js/figma_app/views/fullscreen/fullscreen_menu/ai_balance_menu_card.tsx
 * as well. Credit rules and copy should stay in the shared Figma view model.
 */
export function FigmaEmbedAiBalanceCard({ viewModel }: FigmaEmbedAiBalanceCardProps) {
  const creditsLabelId = useId();
  const { meterMax, meterValue, fillPercent } = getBoundedMeterValue(viewModel.meter.value, viewModel.meter.max);

  return (
    <AiBalanceMenuItem>
      {viewModel.headerLines.length > 0 && (
        <>
          <AiBalanceCardHeader id="figma-embed-ai-balance-header">
            {viewModel.headerLines.map((line, index) => (
              <div key={`${line}-${index}`}>{line}</div>
            ))}
          </AiBalanceCardHeader>
          <AiBalanceDivider />
        </>
      )}
      {!viewModel.hideMeter && (
        <AiBalanceCardShell isEmptyBalance={viewModel.isEmptyBalance}>
          <AiBalanceCardTitle
            id={creditsLabelId}
            variant="body-sm-rg"
            hasSubtitle={!!viewModel.subtitle}
            isEmptyBalance={viewModel.isEmptyBalance}
          >
            {viewModel.title}
          </AiBalanceCardTitle>
          {viewModel.subtitle && (
            <AiBalanceCardSubtitle variant="body-sm-rg">{viewModel.subtitle}</AiBalanceCardSubtitle>
          )}
          <AiBalanceMeterTrack
            role="progressbar"
            aria-labelledby={creditsLabelId}
            aria-valuenow={meterValue}
            aria-valuemin={0}
            aria-valuemax={meterMax}
          >
            <AiBalanceMeterFill fillPercent={fillPercent} />
          </AiBalanceMeterTrack>
        </AiBalanceCardShell>
      )}
      <AiBalanceBodyStack>
        {viewModel.bodyLines.map((line, index) => (
          <AiBalanceText key={`${line}-${index}`} variant="body-sm-rg">
            {line}
          </AiBalanceText>
        ))}
        {viewModel.action?.placement === 'body' && <AiBalanceActionView action={viewModel.action} />}
        {viewModel.promotionalFooterLines.length > 0 && (
          <AiBalancePromotionalCredits id="figma-embed-ai-balance-promotional-credits">
            {viewModel.promotionalFooterLines.map((line, index) => (
              <AiBalanceText key={`${line}-${index}`} variant="body-sm-rg">
                {line}
              </AiBalanceText>
            ))}
          </AiBalancePromotionalCredits>
        )}
        {viewModel.action?.placement === 'footer' && <AiBalanceActionView action={viewModel.action} />}
      </AiBalanceBodyStack>
    </AiBalanceMenuItem>
  );
}

function FigmaEmbedAiBalanceStateView({ state }: { state: AiBalanceState }) {
  if (state.kind === 'error') return <AiBalanceErrorState />;
  if (state.kind === 'ok') return <FigmaEmbedAiBalanceCard viewModel={state.viewModel} />;
  return <AiBalanceLoadingState />;
}

interface FigmaEmbedAiBalanceContextValue {
  state: AiBalanceState;
  /** Re-request the AI balance view model from the Figma parent. */
  requestAiBalance: () => void;
  /**
   * Trigger a credit action by posting the matching message to the Figma
   * parent, which runs Figma's canonical AI-balance action (direct
   * sendCreditsRequest, the per-user-cap modal, or the admin manage-credits
   * redirect). `request_credits` sends directly; `user_limit_request_credits`
   * opens the modal — the split mirrors Figma's own logic.
   */
  triggerAiBalanceAction: (actionType: FigmaEmbedAiBalanceMenuCardAction['type']) => void;
}

const FigmaEmbedAiBalanceContext = createContext<FigmaEmbedAiBalanceContextValue>({
  state: { kind: 'loading' },
  requestAiBalance: () => {},
  triggerAiBalanceAction: () => {},
});

/**
 * Owns a single AI-balance subscription for the embed session: requests the
 * view model from the Figma parent and stores the reply. Shared (rather than
 * per-component) so the AI-balance menu and the many run-block surfaces read
 * one resolved credit action without each opening its own listener/request.
 */
export function FigmaEmbedAiBalanceProvider({ children }: { children: ReactNode }) {
  const isFigmaEmbedMode = useIsFigmaEmbedMode();
  const figmaAiMeterHasCredits = useFigmaEmbedAiMeterHasCredits();
  const [state, setState] = useState<AiBalanceState>({ kind: 'loading' });

  const requestAiBalance = useCallback(() => {
    setState((prev) => (prev.kind === 'ok' ? prev : { kind: 'loading' }));
    requestAiBalanceFromParent();
  }, []);

  useEffect(() => {
    if (!isFigmaEmbedMode) return undefined;

    const handleMessage = (event: MessageEvent) => {
      if (!isFigmaEmbedAllowedOrigin(event.origin)) return;

      const data = event.data as AiBalanceRefreshMessage | null;
      if (!data || data.type !== FIGMA_EMBED_MESSAGE_TYPES.inbound.AI_BALANCE_REFRESH) return;

      if (data.loadFailed) {
        setState({ kind: 'error' });
        return;
      }

      if (data.viewModel) {
        setState({ kind: 'ok', viewModel: data.viewModel });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isFigmaEmbedMode]);

  // Fetch on mount and whenever the Figma AI meter flips (e.g. the user runs out
  // of credits), so the run-block "Request credits" UX always has a fresh
  // resolved action to render.
  useEffect(() => {
    if (!isFigmaEmbedMode) return;
    requestAiBalance();
  }, [isFigmaEmbedMode, figmaAiMeterHasCredits, requestAiBalance]);

  const triggerAiBalanceAction = useCallback(
    (actionType: FigmaEmbedAiBalanceMenuCardAction['type']) => {
      switch (actionType) {
        case 'manage_credits':
          postAiBalanceActionToParent(FIGMA_EMBED_MESSAGE_TYPES.outbound.AI_BALANCE_MANAGE_CREDITS);
          break;
        case 'request_credits':
          postAiBalanceActionToParent(FIGMA_EMBED_MESSAGE_TYPES.outbound.AI_BALANCE_REQUEST_CREDITS);
          break;
        case 'user_limit_request_credits':
          postAiBalanceActionToParent(FIGMA_EMBED_MESSAGE_TYPES.outbound.AI_BALANCE_USER_LIMIT_REQUEST_CREDITS);
          break;
        case 'pending_request':
          break;
      }
      // Re-fetch so the CTA reflects the resulting pending state.
      requestAiBalance();
    },
    [requestAiBalance],
  );

  const value = useMemo(
    () => ({ state, requestAiBalance, triggerAiBalanceAction }),
    [state, requestAiBalance, triggerAiBalanceAction],
  );

  return <FigmaEmbedAiBalanceContext.Provider value={value}>{children}</FigmaEmbedAiBalanceContext.Provider>;
}

export function useFigmaEmbedAiBalance(): FigmaEmbedAiBalanceContextValue {
  return useContext(FigmaEmbedAiBalanceContext);
}

/**
 * The resolved AI-balance credit action for the current user, or `null` when
 * the user has credits / the balance hasn't resolved. Used by run-block
 * surfaces to decide whether (and which) "Request credits" / "Manage credits"
 * CTA to show.
 */
export function useFigmaEmbedAiBalanceAction(): FigmaEmbedAiBalanceMenuCardAction | null {
  const { state } = useFigmaEmbedAiBalance();
  return state.kind === 'ok' ? state.viewModel.action : null;
}

/**
 * "Request credits" / "Manage credits" CTA for run-block surfaces
 * (ModelNodeFooter / RunModelsSection) when the user is out of Figma AI
 * credits. Renders the resolved action from Figma and posts the matching parent
 * action on click. Returns `null` when there's no actionable CTA. Rendered
 * inside the out-of-credits banner via `FigmaEmbedOutOfCreditsMsg`.
 */
export function FigmaEmbedRunBlockCreditsCta() {
  const action = useFigmaEmbedAiBalanceAction();
  const { triggerAiBalanceAction } = useFigmaEmbedAiBalance();

  if (!action) return null;

  // The request already went out; show the confirmation text, no button.
  if (action.type === 'pending_request') {
    return (
      <Typography variant="body-sm-rg" color={token.text}>
        {action.message}
      </Typography>
    );
  }

  const disabled = action.type === 'user_limit_request_credits' && action.hasPendingRequest;
  const label =
    action.type === 'manage_credits' || action.type === 'request_credits'
      ? action.label
      : action.hasPendingRequest
        ? action.pendingMessage
        : action.message;

  return (
    <AiBalanceActionButtonRoot
      size="small"
      disabled={disabled}
      onClick={() => triggerAiBalanceAction(action.type)}
      sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
    >
      {label}
    </AiBalanceActionButtonRoot>
  );
}

/**
 * `AI balance ▶` submenu surfaced in the WorkflowFileMenu when running inside
 * the Figma embed iframe. It requests fresh AI credit state from the Figma
 * parent each time the submenu opens.
 */
function FigmaEmbedAiBalanceMenu({ onClose }: FigmaEmbedAiBalanceMenuProps) {
  const { t } = useTranslation();
  const { requestAiBalance, state } = useFigmaEmbedAiBalance();

  return (
    <ParentMenuItem
      id="figma-embed-ai-balance"
      label={t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.FIGMA_EMBED_AI_BALANCE)}
      onClose={onClose}
      onOpen={requestAiBalance}
      contextMenuWidth="208px"
      offset={{ left: '10px', top: '-9px' }}
    >
      <FigmaEmbedAiBalanceStateView state={state} />
    </ParentMenuItem>
  );
}

export default FigmaEmbedAiBalanceMenu;
